import React, { useState, useEffect } from "react";
import {
  AlertTriangle,
  AlertCircle,
  Info,
  Bell,
  X,
  ChevronRight,
  RefreshCw,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { insightsAPI } from "@/api";
import { Insight, InsightSummary } from "@/types";
import { useDateRange } from "@/contexts/DateRangeContext";
import { formatDateForAPI } from "@/lib/date-utils";

const SEVERITY_CONFIG = {
  critical: {
    icon: AlertTriangle,
    color: "text-red-500",
    bgColor: "bg-red-50 dark:bg-red-950",
    borderColor: "border-red-200 dark:border-red-800",
    badge: "destructive" as const,
  },
  warning: {
    icon: AlertCircle,
    color: "text-yellow-500",
    bgColor: "bg-yellow-50 dark:bg-yellow-950",
    borderColor: "border-yellow-200 dark:border-yellow-800",
    badge: "warning" as const,
  },
  info: {
    icon: Info,
    color: "text-blue-500",
    bgColor: "bg-blue-50 dark:bg-blue-950",
    borderColor: "border-blue-200 dark:border-blue-800",
    badge: "secondary" as const,
  },
};

interface InsightsCardProps {
  locationId?: string;
  maxItems?: number;
  onViewAll?: () => void;
}

export function InsightsCard({
  locationId,
  maxItems = 5,
  onViewAll,
}: InsightsCardProps) {
  const { dateRange } = useDateRange();
  const [insights, setInsights] = useState<Insight[]>([]);
  const [summary, setSummary] = useState<InsightSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const startDateStr = formatDateForAPI(dateRange.from);
  const endDateStr = formatDateForAPI(dateRange.to);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const [insightsRes, summaryRes] = await Promise.all([
        insightsAPI.getInsights({
          per_page: maxItems,
          location_id: locationId,
          unread_only: false,
          include_dismissed: false,
        }),
        insightsAPI.getSummary({ location_id: locationId, days: 7 }),
      ]);

      if (insightsRes.data) {
        setInsights(Array.isArray(insightsRes.data) ? insightsRes.data : []);
      }
      if (summaryRes.data) {
        setSummary(summaryRes.data);
      }
    } catch (error) {
      console.error("Failed to fetch insights:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInsights();
  }, [locationId, maxItems, startDateStr, endDateStr]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await insightsAPI.generate({
        start_date: startDateStr,
        end_date: endDateStr,
        location_id: locationId,
      });
      await fetchInsights();
    } catch (error) {
      console.error("Failed to generate insights:", error);
    } finally {
      setGenerating(false);
    }
  };

  const handleDismiss = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await insightsAPI.dismiss(id);
      setInsights((prev) => prev.filter((i) => i.id !== id));
      if (summary) {
        setSummary({
          ...summary,
          total: summary.total - 1,
          unread: Math.max(0, summary.unread - 1),
        });
      }
    } catch (error) {
      console.error("Failed to dismiss insight:", error);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Bell className="h-5 w-5" />
            Insights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            Loading insights...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Bell className="h-5 w-5" />
            Insights
            {summary && summary.unread > 0 && (
              <Badge variant="destructive" className="ml-2">
                {summary.unread} new
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleGenerate}
              disabled={generating}
            >
              <RefreshCw
                className={`h-4 w-4 mr-1 ${generating ? "animate-spin" : ""}`}
              />
              {generating ? "Generating..." : "Refresh"}
            </Button>
            {onViewAll && (
              <Button size="sm" variant="ghost" onClick={onViewAll}>
                View All
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
        {summary && (
          <div className="flex gap-4 text-sm text-muted-foreground mt-2">
            {summary.critical > 0 && (
              <span className="flex items-center gap-1 text-red-500">
                <AlertTriangle className="h-3 w-3" />
                {summary.critical} critical
              </span>
            )}
            {summary.warning > 0 && (
              <span className="flex items-center gap-1 text-yellow-500">
                <AlertCircle className="h-3 w-3" />
                {summary.warning} warnings
              </span>
            )}
            {summary.info > 0 && (
              <span className="flex items-center gap-1 text-blue-500">
                <Info className="h-3 w-3" />
                {summary.info} info
              </span>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {insights.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Info className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No insights available</p>
            <p className="text-sm mt-1">
              Click Refresh to generate new insights
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {insights.map((insight) => {
              const config = SEVERITY_CONFIG[insight.severity];
              const Icon = config.icon;

              return (
                <div
                  key={insight.id}
                  className={`relative p-3 rounded-lg border ${config.bgColor} ${config.borderColor} ${
                    !insight.is_read
                      ? "ring-2 ring-offset-1 ring-primary/20"
                      : ""
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Icon
                      className={`h-5 w-5 mt-0.5 ${config.color} flex-shrink-0`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm truncate">
                          {insight.title}
                        </span>
                        <Badge
                          variant={config.badge}
                          className="text-xs flex-shrink-0"
                        >
                          {insight.severity}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {insight.summary}
                      </p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                        <span>{formatDate(insight.insight_date)}</span>
                        {insight.location_id && (
                          <>
                            <span>•</span>
                            <span>Zone {insight.location_id}</span>
                          </>
                        )}
                        <span>•</span>
                        <span className="capitalize">{insight.category}</span>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 flex-shrink-0"
                      onClick={(e) => handleDismiss(insight.id, e)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
